import React, { useState, useEffect } from "react";
import { WizardData } from "./OfferWizard";
import PublicOfferPreview from "../PublicOfferPreview";
import { publishOffer, getOfferDetails } from "../../services/api";

interface SummaryStepProps {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
  onPrev: () => void;
  onFinish: () => void;
}

interface PublishConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const PublishConfirmationModal: React.FC<PublishConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4">Publikacja oferty</h3>
        <div className="space-y-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="text-yellow-600 mt-0.5">⚠️</div>
              <div>
                <p className="text-yellow-800 font-medium mb-2">
                  Uwaga! Po publikacji oferta zostanie zamknięta do edycji.
                </p>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Oferta otrzyma unikalny publiczny link</li>
                  <li>• Nie będzie można już edytować jej zawartości</li>
                  <li>• Klient będzie mógł ją przeglądać online</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Publikuję...
              </>
            ) : (
              "Opublikuj ofertę"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface PublishSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicToken: string;
  mainOfferPublicToken?: string;
}

const PublishSuccessModal: React.FC<PublishSuccessModalProps> = ({
  isOpen,
  onClose,
  publicToken,
  mainOfferPublicToken,
}) => {
  if (!isOpen) return null;

  // Use mainOfferPublicToken if available (for versions), otherwise use publicToken
  const tokenToUse = mainOfferPublicToken || publicToken;
  const publicUrl = `${window.location.origin}/offers/public/${tokenToUse}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicUrl);
    // You could add a toast notification here
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="text-green-600 text-2xl">✓</div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Oferta została opublikowana!
          </h3>
          <p className="text-gray-600">
            Twoja oferta jest teraz dostępna pod publicznym linkiem
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Link publiczny:
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={publicUrl}
              readOnly
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            />
            <button
              onClick={copyToClipboard}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
            >
              Kopiuj
            </button>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => window.open(publicUrl, "_blank")}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Zobacz opublikowaną ofertę
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Wróć do listy ofert
          </button>
        </div>
      </div>
    </div>
  );
};

const SummaryStep: React.FC<SummaryStepProps> = ({
  data,
  updateData,
  onPrev,
  onFinish,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedToken, setPublishedToken] = useState<string>("");
  const [offerData, setOfferData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOfferData = async () => {
      if (!data.offerId) {
        setError("Brak ID oferty");
        setLoading(false);
        return;
      }

      try {
        const offer = await getOfferDetails(data.offerId);
        setOfferData(offer);
        setError(null);
      } catch (err) {
        console.error("Błąd podczas ładowania danych oferty:", err);
        setError("Nie udało się załadować danych oferty");
      } finally {
        setLoading(false);
      }
    };

    fetchOfferData();
  }, [data.offerId]);

  const handlePublishClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmPublish = async () => {
    if (!data.offerId) {
      alert("Błąd: Brak ID oferty");
      return;
    }

    setIsPublishing(true);
    try {
      const publishedOffer = await publishOffer(data.offerId);
      setPublishedToken(publishedOffer.publicToken);
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Błąd podczas publikacji oferty:", error);
      alert("Nie udało się opublikować oferty. Spróbuj ponownie.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    onFinish(); // Przekieruj do listy ofert
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card p-4">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-neutral-600">
              Ładowanie podglądu oferty...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="card p-4">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg
                className="w-12 h-12 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-lg font-semibold">Błąd ładowania</p>
              <p className="text-sm text-neutral-600 mt-2">{error}</p>
            </div>
            <button onClick={onPrev} className="btn-secondary">
              Wróć do poprzedniego kroku
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Public Offer Preview */}
      <div className="card p-4">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">
            Podgląd Oferty
          </h2>
          <p className="text-neutral-600">
            Tak będzie wyglądała Twoja oferta dla klienta
          </p>
        </div>

        {/* Preview Frame */}
        <div className="">
          {offerData ? (
            <PublicOfferPreview
              offer={offerData}
              brandKit={offerData?.hotel?.brandKit}
              termsAndConditions={
                offerData?.hotel?.offerSections?.termsAndConditions
              }
              editMode={false}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Brak danych do wyświetlenia</div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between space-x-4">
        <button onClick={onPrev} className="btn-secondary">
          Wstecz - Plan Pobytu
        </button>
        <div className="flex space-x-3">
          <button className="btn-outline">Zapisz jako szablon</button>
          <button onClick={handlePublishClick} className="btn-success">
            Opublikuj ofertę
          </button>
        </div>
      </div>

      {/* Modals */}
      <PublishConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmPublish}
        isLoading={isPublishing}
      />

      <PublishSuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        publicToken={publishedToken}
      />
    </div>
  );
};

export default SummaryStep;
